import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  inRange,
  iso,
  lastDays,
  monthGrid,
  mskToday,
  PRESETS,
  ru,
  руКратко,
  shift,
  titleOf,
  yesterday,
  type Period,
} from './period';
import { IconChevron } from '../ui/icons';
import { K } from '../kabinet/atoms';
import { placeBelow } from '../ui/place';

/**
 * Выбор периода: готовые промежутки слева, календарь справа.
 *
 * ПЕРЕСОБРАН ПО ЭТАЛОНУ. Прежняя редакция брала оттуда только идею
 * «слева готовое, справа календарь», а всё остальное решала на месте —
 * и разошлась в трёх вещах, каждая из которых меняет работу, а не вид:
 *
 * 1. НАБРАННЫЙ ПРОМЕЖУТОК ВИДЕН В ПОЛЕ «с → по», И ЕСТЬ «ГОТОВО».
 *    Раньше окно применяло выбор само, на втором нажатии по числу,
 *    и закрывалось. Промахнулся вторым нажатием — период применён,
 *    окно закрыто, открывай заново. Теперь оба края видны словами,
 *    пока набираешь, и уходят на экран только по «Готово».
 * 2. ДВА МЕСЯЦА РЯДОМ. Промежуток почти всегда пересекает границу
 *    месяца («последние две недели» в начале сентября — это конец
 *    августа), и на одном месяце его надо набирать через перелистывание,
 *    вслепую.
 * 3. ПРОМЕЖУТОК — ПОЛОСА, А НЕ ЦЕПОЧКА КРУЖКОВ. Клетки стоят вплотную
 *    и скругляются только по краям выбранного; раньше между ними был
 *    зазор, и выбранное рассыпалось на отдельные дни.
 *
 * БУДУЩЕЕ НЕ ВЫБИРАЕТСЯ ВОВСЕ. Данных за завтра нет, и предлагать его
 * значит предлагать пустой экран.
 */

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const WEEK = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

/** Ширина окна периода — эталонная (`calStyle`: `width: 640px`). */
export const ШИРИНА_ОКНА_ПЕРИОДА = 640;
/** Отбивка окна от кнопки — эталонная (`top: calc(100% + 6px)`). */
const ОТБИВКА_ОКНА = 6;

export function PeriodPicker({
  value,
  onChange,
  вКорне = false,
  узел = 'кнопка-периода',
  maxDay,
}: {
  value: Period;
  /** Optional reporting cutoff; bidder behavior stays unchanged. */
  maxDay?: string;
  onChange: (p: Period) => void;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * ИМЯ УЗЛА — ПАРАМЕТРОМ, ПОТОМУ ЧТО У ДИЗАЙНЕРА ИХ ДВА.
   *
   * На экранах биддера и запросов кнопка периода называется
   * `кнопка-периода`, а на экране «Финансовая аналитика» —
   * `выбор-периода`. Это не описка дизайнера: в инвентаре
   * (`04-DATA-D-imena.md`, передача 13.09.2026) стоят ОБА имени,
   * каждое со своим экраном, и в разметке эталона каждое встречается
   * ровно один раз — под своим `data-screen-label`.
   *
   * Сторож имён (`src/site/data-d.spec.ts`) сверяет наши узлы
   * с инвентарём. С одним именем на оба экрана второе осталось бы
   * ненайденным — а искать его глазами в тридцати тысячах строк
   * эталона нельзя.
   * ═══════════════════════════════════════════════════════════════ */
  узел?: string;
  /**
   * ═══════════════════════════════════════════════════════════════════
   * РИСОВАТЬ ОКНО В КОРНЕ СТРАНИЦЫ, А НЕ РЯДОМ С КНОПКОЙ.
   *
   * 10.09.2026 владелец: «в кластерах не работают диапазоны дат,
   * нажимаешь и ничего не происходит».
   *
   * И не происходило. Проба браузером на 1440 и 1280: окно в разметке
   * ЕСТЬ, `isVisible` отдаёт `true`, а коробка его — y = 1006 при
   * высоте окна браузера 1000. То есть окно уезжало за нижний край
   * экрана целиком.
   *
   * ПОЧЕМУ. Окно стоит `position: absolute; top: calc(100% + 6px)`,
   * и «100 %» считается от БЛИЖАЙШЕГО РАСПОЛОЖЕННОГО предка. В биддере
   * это ряд действий (`k-ряд-действий-экрана`, `position: relative`) —
   * 34 точки, и окно встаёт под кнопкой. На экране запросов такого
   * предка рядом нет вовсе: ближайший — колонка экрана высотой 911,
   * и «100 %» от неё выносит окно ниже сгиба. Сверх того его режут
   * три предка подряд: полоса отбора (`overflow-x: auto`), карточка
   * (`overflow: hidden`) и колонка экрана.
   *
   * ЛЕЧИТСЯ ТЕМ ЖЕ, ЧЕМ УЖЕ ВЫЛЕЧЕНЫ «КОЛОНКИ» НА ЭТОМ ЖЕ ЭКРАНЕ:
   * всё, что вылезает за край родителя с прокруткой, рисуется только
   * в корне страницы и считается от коробки кнопки (`placeBelow`).
   *
   * ПОЧЕМУ НЕ ВСЕГДА. В биддере окно прижато правым краем к РЯДУ
   * действий, а не к кнопке периода: за ней стоят ещё три кнопки,
   * и эталон рисует окно у правого края всего ряда. Переведи я туда
   * тот же корневой расчёт — окно уехало бы влево на две сотни точек
   * и разошлось бы с эталоном там, где сейчас сходится.
   * ═══════════════════════════════════════════════════════════════════
   */
  вКорне?: boolean;
}) {
  const [open, setOpen] = useState(false);
  /** Левый из двух показанных месяцев. */
  const [anchor, setAnchor] = useState<Date>(() => addMonths(mskToday(), -1));
  /**
   * Что набрано в окне, но ещё не применено.
   *
   * ПОКА ОКНО ОТКРЫТО, ЭКРАН НЕ МЕНЯЕТСЯ. Иначе каждое нажатие
   * по числу перезапрашивает всю страницу под окном — а нажатий
   * на один выбор минимум два.
   */
  const [draft, setDraft] = useState<Period>(value);
  /** Первое нажатие: второй край ещё не выбран. */
  const [half, setHalf] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  /** Кнопка — от неё считается место окна, когда оно рисуется в корне. */
  const [кнопка, setКнопка] = useState<HTMLButtonElement | null>(null);
  /**
   * Само окно.
   *
   * НУЖЕН ОТДЕЛЬНЫЙ ССЫЛ, ПОТОМУ ЧТО В КОРНЕ ОКНО НЕ ЛЕЖИТ ВНУТРИ
   * `box`: «нажали мимо — закрыть» проверяло бы только кнопку и
   * закрывало окно на первом же нажатии по числу.
   */
  const окно = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const away = (e: MouseEvent) => {
      const цель = e.target as Node;
      if (box.current?.contains(цель)) return;
      if (окно.current?.contains(цель)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', esc);
    document.addEventListener('mousedown', away);
    return () => {
      document.removeEventListener('keydown', esc);
      document.removeEventListener('mousedown', away);
    };
  }, [open]);

  const today = maxDay ?? iso(mskToday());
  const presetNow = maxDay ? new Date(maxDay + 'T12:00:00+03:00') : new Date();

  const show = () => {
    // Открываем на том месяце, где стоит выбранное, а не на текущем:
    // «показать июнь» и тут же листать назад — работа впустую.
    const end = new Date(`${value.to}T00:00:00Z`);
    setAnchor(addMonths(end, -1));
    setDraft(value);
    setHalf(null);
    setOpen(true);
  };

  const pick = (day: string) => {
    if (half === null) {
      setHalf(day);
      setDraft({ from: day, to: day });
      return;
    }
    // Справа налево — не ошибка, а другой порядок нажатий.
    const [from, to] = half <= day ? [half, day] : [day, half];
    setHalf(null);
    setDraft({ from, to });
  };

  const preset = (p: Period) => {
    setHalf(null);
    setDraft(p);
  };

  const done = () => {
    setOpen(false);
    setHalf(null);
    if (draft.from !== value.from || draft.to !== value.to) onChange(draft);
  };

  const months = [anchor, addMonths(anchor, 1)];
  const lastDate = maxDay ? new Date(maxDay + 'T00:00:00Z') : mskToday();
  const nextBlocked = !canAdvanceCalendar(anchor, lastDate);

  /**
   * Место окна на экране, когда оно рисуется в корне.
   *
   * ПРИЖИМАЕМСЯ ПРАВЫМ КРАЕМ К КНОПКЕ (`'right'`) — так же, как окно
   * прижато к правому краю ряда в биддере. Попадание в экран считает
   * общая `placeBelow`, своей арифметики здесь нет: она уже дважды
   * оказывалась неполной у соседних меню.
   *
   * ОТБИВКА 6, А НЕ 4 ПО УМОЛЧАНИЮ: у эталона `top: calc(100% + 6px)`.
   *
   * НА УЗКОМ МЕСТА НЕТ ВОВСЕ, И ЭТО НЕ ПРОПУСК. Там окно — полка снизу
   * (`position: fixed; left 8; right 8; bottom 8` в правиле для 1023
   * и уже), и посчитанные числа его бы сломали. Числа уезжают
   * ПЕРЕМЕННЫМИ, а не полями `top`/`left`: поле в разметке перебило бы
   * правило разрешения, а переменная — нет.
   */
  const узко = typeof window !== 'undefined' && window.innerWidth < 1024;
  const место = (() => {
    if (!вКорне || !open || !кнопка || узко) return null;
    return placeBelow(
      кнопка.getBoundingClientRect(),
      ШИРИНА_ОКНА_ПЕРИОДА,
      { width: window.innerWidth, height: window.innerHeight },
      'right',
      ОТБИВКА_ОКНА,
    );
  })();

  /**
   * Само окно — переменной, а не вложенной функцией.
   *
   * Функция, объявленная ПОСЛЕ `return`, — ровно то, за чем следят
   * `pravilo-hukov.spec.ts` и `rannii-vozvrat.spec.ts`: читающий видит
   * возврат и считает, что дальше кода нет.
   */
  const окноПериода = (

      /* ИМЯ УЗЛА — ЭТАЛОННОЕ (`окно-периода`). Без него состояние
         «окно периода» лежало в замере эталона снятым и не читалось
         ни разу: сверке нечего было искать. */
        <div
          ref={окно}
          data-d="окно-периода"
          className={'rk-card rk-period-cal overflow-hidden' + (вКорне ? ' rk-period-cal-v-korne' : '')}
          /* ═══════════════════════════════════════════════════════════
             ЧИСЛА ПОЛЯМИ, И ТОЛЬКО НА ШИРОКОМ.

             На узком `место` не считается вовсе (см. довод у него),
             и полку снизу рисует таблица стилей. Поставь мы `top`
             и там — поле в разметке перебило бы правило разрешения
             и полка встала бы посреди экрана.
             ═══════════════════════════════════════════════════════ */
          style={
            место === null
              ? { boxShadow: 'var(--shadow-3)' }
              : { boxShadow: 'var(--shadow-3)', top: место.top, left: место.left }
          }
        >
          {/* ШАПКА: ЧТО НАБРАНО И КНОПКА «ГОТОВО». Пока оба края видны
              словами, промах вторым нажатием стоит одного нажатия,
              а не открытия окна заново. */}
          <div
            className="flex items-center rk-period-head"
            style={{
              gap: 'var(--sp-2)',
              padding: 'var(--sp-3)',
              borderBottom: 'var(--hair) solid var(--line)',
            }}
          >
            <span
              className="flex flex-1 items-center"
              style={{
                gap: 'var(--sp-2)',
                border: 'var(--hair) solid var(--brand)',
                borderRadius: 'var(--r-sm)',
                padding: '6px 10px',
                boxShadow: '0 0 0 3px var(--brand-ring)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <span>{ru(draft.from)}</span>
              <span className="rk-faint">→</span>
              <span style={{ color: half === null ? 'var(--ink)' : 'var(--ink-3)' }}>
                {half === null ? ru(draft.to) : 'выберите вторую дату'}
              </span>
              <span className="ml-auto rk-faint">
                <Calendar />
              </span>
            </span>
            <button type="button" className="rk-button" onClick={done}>
              Готово
            </button>
          </div>

          <div className="flex items-stretch rk-period-layout">
            {/* ГОТОВЫЕ ПРОМЕЖУТКИ СЛЕВА, как в кабинете WB: ими пользуются
                в девяти случаях из десяти, и до них не должно быть
                ни одного лишнего движения. */}
            <div
              className="flex flex-col rk-period-presets"
              style={{
                gap: 2,
                padding: 'var(--sp-3) var(--sp-2)',
                borderRight: 'var(--hair) solid var(--line)',
                flex: '0 0 auto',
                minWidth: 'var(--period-preset-width, 152px)',
              }}
            >
              {!maxDay && <PresetButton title="Сегодня" period={lastDays(1)} draft={draft} onPick={preset} />}
              <PresetButton title="Вчера" period={yesterday()} draft={draft} onPick={preset} />
              {PRESETS.filter((p) => p.days > 1).map((p) => (
                <PresetButton
                  key={p.key}
                  title={p.title}
                  period={lastDays(p.days, presetNow)}
                  draft={draft}
                  onPick={preset}
                />
              ))}
            </div>

            <div style={{ padding: 'var(--sp-3)', minWidth: 0 }}>
              <div className="mb-2 flex items-center" style={{ gap: 'var(--sp-2)' }}>
                <Step title="Год назад" onClick={() => setAnchor(addMonths(anchor, -12))}>
                  «
                </Step>
                <Step title="Месяц назад" onClick={() => setAnchor(addMonths(anchor, -1))}>
                  ‹
                </Step>
                <span className="flex-1" />
                <Step
                  title="Месяц вперёд"
                  disabled={nextBlocked}
                  onClick={() => setAnchor(addMonths(anchor, 1))}
                >
                  ›
                </Step>
                <Step
                  title="Год вперёд"
                  disabled={nextBlocked}
                  onClick={() => setAnchor(min(addMonths(anchor, 12), addMonths(lastDate, -1)))}
                >
                  »
                </Step>
              </div>

              {/* ДВА МЕСЯЦА РЯДОМ: промежуток почти всегда пересекает
                  границу месяца, и набирать его через перелистывание
                  значит выбирать второй край вслепую.

                  ═══════════════════════════════════════════════════════
                  НА ТЕЛЕФОНЕ МЕСЯЦ ОДИН — ТАК В ЭТАЛОНЕ, И ИНАЧЕ ВЫБОР
                  ТАМ ПРОСТО НЕ РАБОТАЛ.

                  Полка снизу на 390 — 364 точки шириной и закрыта
                  `overflow: hidden`; два месяца в неё не помещаются,
                  и второй уезжал за край молча. Проба: 61 кнопка-число
                  «видима» по разметке, а достать половину из них нельзя.

                  Эталон на 390 рисует ОДИН месяц — первый из двух
                  (снимок `окно-периода--390`: «Июль» против «Июль ·
                  Август» на 1440). Прячется он правилом, а не ветвью
                  в разметке: ширину знает таблица стилей, а не React,
                  и вторая копия условия разошлась бы с первой.
                  ═══════════════════════════════════════════════════ */}
              <div className="flex rk-period-months" style={{ gap: 'var(--sp-5)' }}>
                {months.map((m) => (
                  <Month
                    key={iso(m)}
                    month={m}
                    period={draft}
                    today={today}
                    onPick={pick}
                  />
                ))}
              </div>
            </div>
          </div>

          <p
            className="rk-text-xs rk-faint"
            style={{
              padding: 'var(--sp-2) var(--sp-4) var(--sp-3)',
              borderTop: 'var(--hair) solid var(--line)',
            }}
          >
            Время московское — как и сутки в отчётах Wildberries.
          </p>
        </div>
  );

  return (
    /* ═══════════════════════════════════════════════════════════════
       ОБЁРТКА НЕ УЧАСТВУЕТ В ВЫКЛАДКЕ (`display: contents`).

       Раньше здесь стоял `<div class="relative">`, и он ломал ряд
       действий: кнопка периода переставала быть его гибким ребёнком,
       растягивалась на всю ширину обёртки и выходила 371 пиксель
       против эталонных 235. В эталоне никакой обёртки нет — кнопка
       лежит прямо в ряду, а `position: relative` стоит на самом ряду
       (`k-ряд-действий-экрана`), и окно календаря цепляется за него.

       `ref` на обёртке остаётся: он нужен для «нажали мимо — закрыть»,
       а `display: contents` этому не мешает — узел в дереве есть,
       просто не рисуется.
       ═══════════════════════════════════════════════════════════════ */
    <div ref={box} style={{ display: 'contents' }}>
      <button
        type="button"
        data-d={узел}
        ref={setКнопка}
        onClick={() => (open ? setOpen(false) : show())}
        /* ОТКРЫТОЕ ОКНО ДЕРЖИТ КНОПКУ НАЖАТОЙ — так в эталоне
           (подложка `--brand-wash`, рамка `--brand`), и так уже сделано
           у кнопки профиля. Здесь этого не было: окно календаря
           открыто, а кнопка белая с серой рамкой, будто ни при чём,
           и непонятно, чьё это окно. Частица вырезана из состояния
           «окно периода» эталона. */
        className={open ? K.кнопкаПериодаОткрыта : K.кнопкаПериода}
        title="Период, за который считаются деньги и на плитках, и в списке"
      >
        <Calendar />

        {/* ═══════════════════════════════════════════════════════════
            НА УЗКОМ ОСТАЁТСЯ ТОЛЬКО ПРОМЕЖУТОК, И БЕЗ ГОДА.

            Так делает эталон: `periodBtnLabel: narrow ? periodShort
            : period`. И так приходится делать: «Последние 7 дней ·
            17.08.2026 — 23.08.2026» плюс «Синхронизировать» — это
            585 пикселей в коробке шириной 350, и на 390 вбок уезжала
            ВСЯ страница вместе с шапкой. Нашла проба
            `probe/kabinet-edge.mjs`.
            ═══════════════════════════════════════════════════════ */}
        {/* ═══════════════════════════════════════════════════════════
            НА КНОПКЕ ТОЛЬКО ПРОМЕЖУТОК — И ЭТО ЭТАЛОН, А НЕ ЭКОНОМИЯ.

            Здесь стояло ещё и название («Последние 7 дней») жирным,
            и кнопка выходила 371 пиксель против эталонных 235:
            на снимке рядом она первой бросается в глаза.

            Название периода человеку на кнопке не нужно: он и так
            только что его выбрал, а даты отвечают на вопрос, ради
            которого на кнопку смотрят, — «за что посчитано».
            Само название стоит внутри окна, где выбирают.

            Разделитель — стрелка, как в эталоне на широком, и тире
            на узком (`09.08 — 15.08`): у стрелки с обеих сторон свои
            отбивки, и на 390 они не помещаются.
            ═══════════════════════════════════════════════════════ */}
        <span className="rk-only-wide">
          {ru(value.from)} → {ru(value.to)}
        </span>
        <span className="rk-only-narrow">
          {руКратко(value.from)} — {руКратко(value.to)}
        </span>

        <IconChevron size={12} dir="down" />
      </button>

      {open &&
        (вКорне
          ? createPortal(
              /* ═══════════════════════════════════════════════════════
                 `data-kab` СТОИТ НА КОРНЕ ПОРТАЛА, А НЕ НА САМОМ ОКНЕ.

                 Правила кабинета объявлены как `[data-kab] .k-…`,
                 и до узла, вынесенного в `body`, они иначе не достают —
                 календарь вышел бы голым. Своей выкладки у обёртки нет:
                 оба её ребёнка закреплены на экране.
                 ═══════════════════════════════════════════════════ */
              <div data-kab="true">
                {/* Подложка — та же, что у «Колонок» рядом: без неё
                    нажатие мимо ловится только слушателем документа,
                    а он на телефоне срабатывает не всегда. */}
                <div className="fixed inset-0 z-[900]" onClick={() => setOpen(false)} />
                {окноПериода}
              </div>,
              document.body,
            )
          : окноПериода)}
    </div>
  );
}

function Month({
  month,
  period,
  today,
  onPick,
}: {
  month: Date;
  period: Period;
  today: string;
  onPick: (day: string) => void;
}) {
  return (
    <div className="flex flex-col" style={{ flex: '1 1 0', minWidth: 0 }}>
      <div className="mb-2 text-center rk-text-sm" style={{ fontWeight: 600 }}>
        {MONTHS[month.getUTCMonth()]} {month.getUTCFullYear()}
      </div>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(7,1fr)' }}>
        {WEEK.map((w) => (
          <span key={w} className="py-1 text-center rk-text-xs rk-faint">
            {w}
          </span>
        ))}
      </div>
      {rows(monthGrid(month.getUTCFullYear(), month.getUTCMonth())).map((week, i) => (
        <div key={i} className="grid" style={{ gridTemplateColumns: 'repeat(7,1fr)' }}>
          {week.map((day, j) =>
            day === null ? (
              <span key={j} className="rk-day" />
            ) : (
              <button
                key={j}
                type="button"
                disabled={day > today}
                onClick={() => onPick(day)}
                className={dayClass(day, period, day === today)}
              >
                {Number(day.slice(8))}
              </button>
            ),
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Как выглядит день.
 *
 * СКРУГЛЕНИЕ ТОЛЬКО ПО КРАЯМ ПРОМЕЖУТКА — от этого он читается одной
 * полосой «с 10 по 16», а не десятком отдельно выбранных чисел.
 */
export function dayClass(day: string, p: Period, isToday: boolean): string {
  const edge = day === p.from || day === p.to;
  const out = ['rk-day'];
  if (edge) out.push('rk-day-edge');
  else if (inRange(day, p)) out.push('rk-day-in');
  if (day === p.from) out.push('rk-day-from');
  if (day === p.to) out.push('rk-day-to');
  if (isToday) out.push('rk-day-today');
  return out.join(' ');
}

function PresetButton({
  title,
  period,
  draft,
  onPick,
}: {
  title: string;
  period: Period;
  draft: Period;
  onPick: (p: Period) => void;
}) {
  // ВЫБРАННЫЙ ПРОМЕЖУТОК ПОДСВЕЧЕН. Без этого, открыв окно, человек
  // не видит, чем сейчас смотрит, и выбирает заново на всякий случай.
  const on = draft.from === period.from && draft.to === period.to;
  return (
    <button
      type="button"
      onClick={() => onPick(period)}
      aria-pressed={on}
      className={'rk-nav-item' + (on ? ' rk-nav-item-on' : '')}
      style={{ whiteSpace: 'nowrap' }}
    >
      {title}
    </button>
  );
}

function Step({
  children,
  title,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      className="rk-button-mini-square disabled:opacity-25"
    >
      {children}
    </button>
  );
}

function rows<T>(flat: T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < flat.length; i += 7) out.push(flat.slice(i, i + 7));
  return out;
}

function addMonths(d: Date, by: number): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + by, 1));
}

function min(a: Date, b: Date): Date {
  return a.getTime() <= b.getTime() ? a : b;
}

function sameMonth(a: Date, b: Date): boolean {
  return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth();
}

function Calendar() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <path d="M5 1v2M11 1v2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <rect x="2" y="3" width="12" height="11" rx="2" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <rect x="2" y="6" width="12" height="1.4" />
    </svg>
  );
}

// `shift` не используется в разметке, но держит модуль честным:
// сдвиг суток живёт в одном месте и проверен тестами.
void shift;

/** The narrow layout shows only the first month; the last available month must be reachable. */
export function canAdvanceCalendar(anchor: Date, lastDate: Date): boolean {
  return !sameMonth(anchor,lastDate) && Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth(), 1) < Date.UTC(lastDate.getUTCFullYear(), lastDate.getUTCMonth(), 1);
}
